import CacheBase2 from '../CacheBase2';

const MAX_ENTRIES = 200;
const DUP_CHECK_WINDOW = 10;

/**
 * In-memory log entry buffer.
 * Extends CacheBase2 — _items is the live entry list.
 * Max 200 entries, FIFO rotate on overflow.
 * Duplicate detection on source + method against last 10 entries by ems.
 * Does NOT know about localStorage — persistence is LogCacheStorage's responsibility.
 */
class LogCache extends CacheBase2 {

    /**
     * Adds a log entry to the buffer.
     * If source + method matches an entry in the last DUP_CHECK_WINDOW by ems:
     *   updates lastSeen + increments count — no new entry added.
     * @param {{ app, uid, source, method, message, ems }} entry
     */
    add = (entry) => {
        try {
            this._check();

            const dupIndex = this._findDuplicate(entry);
            if (dupIndex >= 0) {
                // Duplicate — update in place instead of inserting
                this._items[dupIndex].lastSeen = entry.ems;
                this._items[dupIndex].count = (this._items[dupIndex].count ?? 1) + 1;
                return true;
            }

            // New entry — prepend with count and lastSeen initialised
            this._items.unshift({ ...entry, count: 1, lastSeen: entry.ems });

            // Drop oldest entry when over limit
            if (this._items.length > MAX_ENTRIES) {
                this._items.pop();
            }

            return true;
        } catch (error) {
            // Log cache must never throw — silent fail
            return false;
        }
    };

    /**
     * Returns all entries and clears the buffer.
     * Call before flushing to BE on reconnect.
     * @returns {{ app, uid, source, method, message, ems, count, lastSeen }[]}
     */
    flush = () => {
        try {
            const entries = this.getAll();
            this.clear();
            return entries;
        } catch (error) {
            return [];
        }
    };

    // ─── private ──────────────────────────────────────────

    /**
     * Finds a duplicate entry in the last DUP_CHECK_WINDOW entries by ems.
     * Duplicate = same source + method.
     * Returns index in _items of the match, or -1.
     * @param {{ source, method }} entry
     * @returns {number}
     */
    _findDuplicate = (entry) => {
        // Sort by ems descending to get the most recent entries regardless of insert order
        const recent = [...this._items]
            .sort((a, b) => b.ems - a.ems)
            .slice(0, DUP_CHECK_WINDOW);

        // Find match by source + method in the recent window
        const match = recent.find(
            (e) => e.source === entry.source && e.method === entry.method
        );

        if (!match) return -1;

        // Return index in original _items for in-place update
        return this._items.indexOf(match);
    };
}

const logCache = new LogCache();
export default logCache;
